import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { fundPeopleUrl } from '@/hooks/useFundAccess';
import { postAuth, putAuth } from '@/api/ApiCalls';
import CustomMenu from '@/components/ui/Menu';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import deleteIcon from '@/assets/icons/delete.svg';
import editIcon from '@/assets/icons/edit.svg';
import crownIcon from '@/assets/icons/crown.svg';
import { AddFundRepresentativeDtoIn } from '@/models/in/AddFundRepresentativeDtoIn';
import { FundPersonDtoOut, FundRemovedPersonDtoOut } from '@/models/out/FundPersonDtoOut';
import { formatDateLocale } from '@/helpers/dateHelper';
import { AddCoordinatorDialog, EditPersonDetailsDialog, PermissionChip, PersonDetailsForm, ReplaceLeadDialog } from './PeopleDialogs';
import { CrownIcon, InfoIcon } from './peopleIcons';
import styles from './People.module.css';

/** The row's ⋮ menu, left out when there is nothing in it. */
const CustomMenuForPerson = ({ items }: { items: Parameters<typeof CustomMenu>[0]['items'] }) => (items.length > 0 ? <CustomMenu items={items} /> : null);

const fullName = (p: { firstName: string | null; lastName: string | null }) => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();

const errorText = (err: unknown, fallback: string) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim() ? err.response.data : fallback;

interface OfficePeopleListProps {
  fundId: number;
  /** The blade's plain list, for a fund «רכזים והרשאות» is off for. */
  fallback: ReactNode;
  /** The blade re-reads the fund after a change, so its other tabs see it too. */
  onChanged: () => void;
}

/**
 * The office's view of a fund's people, in the fund panel of «ניהול פרויקטים»: the same list the
 * lead coordinators work with, with when each person last signed in. The office adds coordinators
 * - always with an Israeli ID - and takes them out, a lead coordinator too since 3.11.2026; roles and
 * permissions are the lead coordinators'. Below the list, who is no longer in the fund and how they
 * left (Druk, 22.10.2026).
 *
 * The list is behind «רכזים והרשאות»'s flag on the server, which answers 403 for a fund it is off
 * for. Then, and on any other failure, the blade shows `fallback` - the plain list it had before.
 */
const OfficePeopleList = ({ fundId, fallback, onChanged }: OfficePeopleListProps) => {
  const { t } = useTranslation();
  const { currentUser, isCurrentUserAdmin } = useAuthContext();
  const queryClient = useQueryClient();
  const peopleUrl = fundPeopleUrl(fundId);
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: people, isLoading, isError } = useGetAuthApi<FundPersonDtoOut[]>(peopleUrl, undefined, { retry: false });
  const { data: removed } = useGetAuthApi<FundRemovedPersonDtoOut[]>(people ? `${peopleUrl}/removed` : '', undefined, { retry: false });

  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<FundPersonDtoOut | null>(null);
  const [appointing, setAppointing] = useState<FundPersonDtoOut | null>(null);
  const [editing, setEditing] = useState<FundPersonDtoOut | null>(null);
  // Editing details is an admin's, as in users management: a new email moves the site account.
  const canEditDetails = isCurrentUserAdmin();
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [peopleUrl] });
    queryClient.invalidateQueries({ queryKey: [`${peopleUrl}/removed`] });
    queryClient.invalidateQueries({ queryKey: [`funds/financial-data/${fundId}/settings-history`] });
    onChanged();
  };

  const add = useMutation({
    mutationFn: (data: AddFundRepresentativeDtoIn) => postAuth<unknown, AddFundRepresentativeDtoIn>(peopleUrl, headers, data),
    onSuccess: () => {
      setAdding(false);
      refresh();
    },
    onError: (err) => setError(errorText(err, t('fundPeople.genericError'))),
  });

  const remove = useMutation({
    mutationFn: ({ key, replacementKey }: { key: string; replacementKey?: string }) =>
      postAuth<unknown, object>(`${peopleUrl}/remove`, headers, { key, replacementKey }),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (err) => setError(errorText(err, t('fundPeople.genericError'))),
  });

  // «מינוי כרכז ראשי» by the office (3.11.2026) - a fund left with no lead gets one without waiting for anybody inside it.
  const appoint = useMutation({
    mutationFn: (key: string) => postAuth<unknown, object>(`${peopleUrl}/make-founder`, headers, { key }),
    onSuccess: () => {
      setAppointing(null);
      refresh();
    },
    onError: (err) => setError(errorText(err, t('fundPeople.genericError'))),
  });

  const edit = useMutation({
    mutationFn: (data: PersonDetailsForm & { key: string }) => putAuth<FundPersonDtoOut, object, typeof data>(`${peopleUrl}/details`, headers, {}, data),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (err) => setError(errorText(err, t('fundPeople.genericError'))),
  });

  if (isLoading) return <p className={styles.sub}>{t('loading')}...</p>;
  if (isError || !people) return <>{fallback}</>;

  const leads = people
    .filter((p) => p.role === 'Founder')
    .map(fullName)
    .filter(Boolean)
    .join(', ');

  // Removing a lead coordinator (3.11.2026): who else leads stays named in the confirmation; with
  // no other lead, the office picks who takes the role from the coordinators who stay.
  const others = removing ? people.filter((p) => p.key !== removing.key) : [];
  const otherLeads = others.filter((p) => p.role === 'Founder');
  const replacementCandidates = others.filter((p) => p.role !== 'Founder');
  const removingLead = removing?.role === 'Founder';
  const mustPickReplacement = removingLead && otherLeads.length === 0 && replacementCandidates.length > 0;
  const removeMessage = (person: FundPersonDtoOut) => {
    const base = t('fundPeople.confirm.removeMessage', { name: fullName(person) || t('fundPeople.unnamed') });
    if (person.role !== 'Founder') return base;
    const after =
      otherLeads.length > 0
        ? t('fundPeople.confirm.removeLeadOthersStay', { founders: otherLeads.map((p) => fullName(p) || t('fundPeople.unnamed')).join(', ') })
        : t('fundPeople.confirm.removeLeadNobodyLeft');
    return `${base} ${after}`;
  };

  const removedLine = (r: FundRemovedPersonDtoOut) => {
    const date = formatDateLocale(r.removedAt);
    if (r.kind === 'LeftThemselves') return t('fundPeople.removed.leftThemselves', { date });
    if (r.kind === 'RemovedByLeadCoordinator') return t('fundPeople.removed.byLead', { name: r.removedByName || t('fundPeople.roleFounder'), date });
    return t('fundPeople.removed.byOffice', { date });
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: 4, paddingBottom: 12, borderBottom: '1px solid var(--border-divider)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span className={styles.name}>{t('fundPeople.peopleCount', { count: people.length })}</span>
          <button
            type="button"
            className={`${styles.pillButton} ${styles.pillGhost}`}
            onClick={() => {
              setError(null);
              setAdding(true);
            }}
          >
            {t('fundPeople.addButton')}
          </button>
        </div>
        <span className={styles.sub}>{t('fundPeople.foundersLine', { founders: leads || t('fundPeople.nobody') })}</span>
        <span className={styles.sub} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <InfoIcon />
          {t('fundPeople.officeHint')}
        </span>
        {error && !adding && !removing && !appointing && !editing && <span className={`${styles.sub} ${styles.subAttention}`}>{error}</span>}
      </div>

      {people.map((person) => {
        const neverIn = person.hasPortalAccess && !person.lastLoginAt;
        return (
          <div
            key={person.key}
            style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 4, padding: '12px 0', borderBottom: '1px solid var(--border-divider)' }}
          >
            <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <div className={styles.cardHead} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span className={styles.name}>{fullName(person) || t('fundPeople.unnamed')}</span>
                {person.role === 'Founder' ? (
                  <span className={`${styles.chip} ${person.founderStatus === 'Active' ? styles.chipFounder : styles.chipFounderPending}`}>
                    <CrownIcon />
                    {t('fundPeople.roleFounder')}
                  </span>
                ) : (
                  <span className={`${styles.chip} ${styles.chipCoordinator}`}>{t('fundPeople.roleCoordinator')}</span>
                )}
                <PermissionChip person={person} />
              </div>
              <div className={styles.sub}>
                {person.email && <bdi dir="ltr">{person.email}</bdi>}
                {person.phone && (
                  <>
                    {' '}
                    · <bdi dir="ltr">{person.phone}</bdi>
                  </>
                )}
                {' · '}
                {!person.email ? (
                  t('fundPeople.noEmailNote')
                ) : !person.hasPortalAccess ? (
                  t('fundPeople.noPortalNote')
                ) : (
                  <span className={neverIn ? styles.subAttention : undefined}>
                    {neverIn ? t('fundPeople.neverLoggedIn') : t('fundPeople.lastLogin', { date: formatDateLocale(person.lastLoginAt!) })}
                  </span>
                )}
              </div>
              {person.role === 'Founder' && person.founderStatus !== 'Active' && (
                <span className={`${styles.sub} ${styles.subAttention}`}>
                  {person.founderBlockedReason ? t('fundPeople.blockedNote') : t('fundPeople.pendingOtherNote')}
                </span>
              )}
            </div>
            <CustomMenuForPerson
              items={[
                ...(canEditDetails
                  ? [
                      {
                        icon: editIcon,
                        text: t('fundPeople.menu.editDetails'),
                        onClick: () => {
                          setError(null);
                          setEditing(person);
                        },
                      },
                    ]
                  : []),
                ...(person.role !== 'Founder'
                  ? [
                      {
                        icon: crownIcon,
                        text: t('fundPeople.menu.makeFounder'),
                        onClick: () => {
                          setError(null);
                          setAppointing(person);
                        },
                      },
                    ]
                  : []),
                // Anyone, a lead coordinator too (3.11.2026) - who then takes the role is settled in the same window.
                {
                  icon: deleteIcon,
                  text: t('fundPeople.menu.remove'),
                  danger: true,
                  onClick: () => {
                    setError(null);
                    setRemoving(person);
                  },
                },
              ]}
            />
          </div>
        );
      })}

      {removed && removed.length > 0 && (
        <details style={{ paddingTop: 12 }}>
          <summary className={styles.name} style={{ cursor: 'pointer' }}>
            {t('fundPeople.removed.title', { count: removed.length })}
          </summary>
          {removed.map((r, index) => (
            <div key={`${r.email ?? ''}-${index}`} style={{ display: 'grid', gap: 2, padding: '10px 0', borderBottom: '1px solid var(--border-divider)' }}>
              <span className={styles.name}>{fullName(r) || t('fundPeople.unnamed')}</span>
              <span className={styles.sub}>
                {r.email && <bdi dir="ltr">{r.email}</bdi>}
                {r.phone && (
                  <>
                    {' '}
                    · <bdi dir="ltr">{r.phone}</bdi>
                  </>
                )}
              </span>
              <span className={styles.sub}>{removedLine(r)}</span>
            </div>
          ))}
        </details>
      )}

      <AddCoordinatorDialog
        open={adding}
        onClose={() => setAdding(false)}
        onSubmit={(data) => {
          setError(null);
          add.mutate(data);
        }}
        isPending={add.isPending}
        error={adding ? error : null}
      />

      {editing && (
        <EditPersonDetailsDialog
          open
          person={editing}
          onClose={() => setEditing(null)}
          onSubmit={(data) => {
            setError(null);
            edit.mutate({ ...data, key: editing.key });
          }}
          isPending={edit.isPending}
          error={error}
        />
      )}

      {appointing && (
        <ConfirmationDialog
          open
          onOpenChange={(open) => !open && setAppointing(null)}
          title={t('fundPeople.confirm.officeMakeFounderTitle')}
          message={t('fundPeople.confirm.officeMakeFounderMessage', { name: fullName(appointing) || t('fundPeople.unnamed') })}
          confirmText="fundPeople.confirm.makeFounderConfirm"
          isLoading={appoint.isPending}
          error={error}
          onConfirm={() => appoint.mutate(appointing.key)}
          onCancel={() => setAppointing(null)}
        />
      )}

      {removing && mustPickReplacement && (
        <ReplaceLeadDialog
          open
          lead={removing}
          candidates={replacementCandidates}
          onClose={() => setRemoving(null)}
          onSubmit={(replacementKey) => {
            setError(null);
            remove.mutate({ key: removing.key, replacementKey });
          }}
          isPending={remove.isPending}
          error={error}
        />
      )}

      {removing && !mustPickReplacement && (
        <ConfirmationDialog
          open
          onOpenChange={(open) => !open && setRemoving(null)}
          title={t(removingLead ? 'fundPeople.confirm.removeLeadTitle' : 'fundPeople.confirm.removeTitle')}
          message={removeMessage(removing)}
          confirmText="fundPeople.confirm.removeConfirm"
          variant="danger"
          isLoading={remove.isPending}
          error={error}
          onConfirm={() => remove.mutate({ key: removing.key })}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

export default OfficePeopleList;
